import { Form } from 'react-bootstrap';
import { BsCameraVideo, BsClock } from 'react-icons/bs';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { FaRegCalendar } from 'react-icons/fa6';
import { PiForkKnifeFill } from 'react-icons/pi';
import { Link } from 'react-router-dom';
import { Child } from '../../../services/endpoints/people/user';
import { Lesson } from '../../../types/lesson.type';
import { formatAddress } from '../../../utils/utils';
import { displaySessionTimeRangeFromFormatted } from '../../../utils/dateUtils';
import './ClassPreview.css';

interface ClassPreviewProps {
    selectedLessons: Lesson[];
}

const formatDateLocal = (date: Date) =>
    date.toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'long',
        day: '2-digit'
    });

const getLessonChildren = (lesson: Lesson): Child[] => {
    const children: Child[] = [];
    lesson.course.orders?.forEach((order) => {
        order.registrations?.forEach((reg) => {
            if (reg.child) children.push(reg.child);
        });
    });
    return children;
};

const LocationDetail: React.FC<{ lesson: Lesson }> = ({ lesson }) => {
    const { course } = lesson;
    const isOnline = Boolean(course.is_online);
    const videoUrl = course.video_url?.trim();

    if (isOnline) {
        return (
            <span style={{ fontWeight: 600, fontSize: '18px' }} className="lesson-detail text-secondary">
                <div className="detail-icon"><BsCameraVideo style={{ color: '#000' }} /></div>
                <div className="detail-text">
                    {videoUrl ? (
                        <a
                            href={videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', color: '#6098B8' }}
                        >
                            Join via Zoom
                        </a>
                    ) : (
                        <span>Online</span>
                    )}
                </div>
            </span>
        );
    }

    return (
        <span style={{ fontWeight: 600, fontSize: '18px' }} className="lesson-detail text-secondary">
            <div className="detail-icon"><FaMapMarkerAlt style={{ color: '#000' }} /></div>
            <div className="detail-text">
                <span>{course.venue.title}</span>
                <br />
                <span>{formatAddress(course?.venue)}</span>
            </div>
        </span>
    );
};

const SingleLessonView: React.FC<{ lesson: Lesson }> = ({ lesson }) => {
    const lessonChildren = getLessonChildren(lesson);
    return (
        <div className="container-fluid mb-4">
            <span style={{ fontWeight: 600, color: '#5E646980' }}>Class Details</span>
            <br />
            <span className={'h5 text-uppercase text-info fw-bolder mb-0'}>
        {lesson.course?.title}
      </span>
            <span style={{ fontWeight: 600, fontSize: '18px' }} className="lesson-detail text-secondary">
        <div className="detail-icon"><FaRegCalendar style={{ color: '#000' }} /></div>
        <div className="detail-text">
          <span>{formatDateLocal(new Date(lesson.date))}</span>
        </div>
      </span>
            <LocationDetail lesson={lesson} />
            <span style={{ fontWeight: 600, fontSize: '18px' }} className="lesson-detail text-secondary">
        <div className="detail-icon"><BsClock style={{ color: '#000' }} /></div>
        <div className="detail-text text-uppercase">
          <span>
            {displaySessionTimeRangeFromFormatted(
                lesson.formatted_time,
                lesson.course?.venue?.location?.timezone
            )}
          </span>
        </div>
      </span>
            <span style={{ fontSize: '18px' }} className="lesson-detail text-secondary">
        <div className="detail-icon"><PiForkKnifeFill style={{ color: '#000' }} /></div>
        <div className="detail-text">
          {lesson.lesson_plans.map((plan, idx) => (
              <Link
                  key={`schedule-lesson-plan-${idx}`}
                  to={`/lesson-plans/${plan.slug}`}
                  className="text-capitalize"
                  style={{ textDecoration: 'none', color: '#6098B8' }}
              >
                  {plan.name}
              </Link>
          ))}
        </div>
      </span>
            <br />
            <span style={{ fontWeight: 600, color: '#5E646980' }}>Children Enrolled:</span>
            <br />
            {lessonChildren.length > 0 && (
                <div className="children-enrolled">
                    {lessonChildren.map((child, idx) => (
                        <Form.Check
                            key={idx}
                            type="checkbox"
                            id={`child-${lesson.id}-${idx}`}
                            label={`${child.first_name} ${child.last_name}`}
                            value={child.id}
                            readOnly
                            checked
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const ClassPreview: React.FC<ClassPreviewProps> = ({ selectedLessons }) => {
    if (selectedLessons.length === 0) {
        return (
            <div className="no-class-container">
                <span className="no-class-label">No class selected.</span>
            </div>
        );
    }

    return (
        <>
            {selectedLessons.length > 1 && (
                <div className="mb-3">
          <span style={{ fontWeight: 600, color: '#5E646980' }}>
            {selectedLessons.length} classes scheduled this day
          </span>
                </div>
            )}
            {selectedLessons.map((lesson) => (
                <SingleLessonView key={lesson.id} lesson={lesson} />
            ))}
        </>
    );
};

export default ClassPreview;
